import React, { useState, useEffect } from 'react';
import { client } from '../client';

// Placeholder for loading state
const LoadingSpinner = () => (
  <div className="flex justify-center items-center h-64">
    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#2a4865]"></div>
  </div>
);

// Placeholder for error state
const ErrorMessage = ({ message }) => (
  <div className="text-center py-10 px-4 bg-red-100 border border-red-400 text-red-700 rounded">
    <p>Error loading staff information:</p>
    <p>{message}</p>
  </div>
);

const StaffContactPage = () => {
  const [pageData, setPageData] = useState(null);
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const pageQuery = `*[_type == "staffContactPage"][0]{
      title,
      bannerImage{
        asset->{
          _id,
          url
        }
      },
      bannerTitle,
      bannerSubtitle,
      mainContentTitle,
      introText,
      generalInfoTitle,
      generalInfoText,
      schoolPhone,
      schoolEmail
    }`;
    const staffQuery = `*[_type == "staff"] | order(department->sortOrder asc, sortOrder asc){
      name,
      position,
      photo{
        asset->{
          _id,
          url
        }
      },
      email,
      phone,
      department->{
        name,
        sortOrder
      }
    }`;
    const departmentsQuery = `*[_type == "department"] | order(sortOrder asc)`;

    setLoading(true);
    Promise.all([
      client.fetch(pageQuery),
      client.fetch(staffQuery),
      client.fetch(departmentsQuery)
    ])
    .then(([pageContent, staffData, departmentData]) => {
      setPageData(pageContent);
      setStaff(staffData || []);
      setDepartments(departmentData || []);
      setLoading(false);
    })
    .catch(err => {
      console.error('Error fetching staff data:', err);
      setError('Failed to fetch page content. Please try again later.');
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!pageData) {
    return <ErrorMessage message="Could not load page content." />;
  }

  // Group staff by department for rendering
  const staffByDepartment = staff.reduce((acc, staffMember) => {
    const deptName = staffMember.department?.name || 'Other';
    if (!acc[deptName]) {
      acc[deptName] = [];
    }
    acc[deptName].push(staffMember);
    return acc;
  }, {});

  const orderedDepartmentNames = departments.map(d => d.name);

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          <img 
            src={pageData.bannerImage?.asset?.url || "/images/homepage/school-exterior.jpg"}
            alt={pageData.bannerTitle || "P.S. 052 School Staff"}
            className="absolute inset-0 w-full h-full object-cover object-center" 
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        {/* Content overlay */}
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle}
              </h1>
              <p className="text-white text-lg md:text-xl opacity-90">
                {pageData.bannerSubtitle}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Introduction */}
            <div className="text-center mb-12">
              <h2 className="text-[#2a4865] text-3xl font-bold mb-4 relative inline-block">
                {pageData.mainContentTitle}
                <span className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-yellow-500"></span>
              </h2>
              <p className="text-gray-700 text-lg max-w-3xl mx-auto">
                {pageData.introText}
              </p>
            </div>

            {/* Staff by Department */}
            {orderedDepartmentNames.map((department) => (
              staffByDepartment[department] && (
                <div key={department} className="mb-12">
                  <h3 className="text-[#691e31] text-2xl font-bold mb-6 relative">
                    {department}
                    <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-yellow-500"></span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {staffByDepartment[department].map((member, idx) => (
                      <div key={idx} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-[#2a4865] rounded-full flex items-center justify-center mr-4 overflow-hidden flex-shrink-0">
                              {member.photo?.asset?.url ? (
                                <img 
                                  src={member.photo.asset.url} 
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-white text-lg font-semibold">
                                  {member.name.split(' ').map(n => n[0]).join('')}
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold text-[#2a4865] mb-1">
                                {member.name}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {member.position}
                              </p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mt-4 space-y-2">
                          {member.email && (
                            <div className="flex items-center text-gray-700">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                              </svg>
                              <a 
                                href={`mailto:${member.email}`} 
                                className="text-[#691e31] hover:underline break-all text-sm"
                              >
                                {member.email}
                              </a>
                            </div>
                          )}
                          {member.phone && (
                            <div className="flex items-center text-gray-700">
                               <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.518.76a11.024 11.024 0 008.57 8.57l.76-1.518a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                              </svg>
                              <span className="text-sm text-gray-600">{member.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ))}

            {/* Contact Information */}
            <div className="mt-16 bg-gray-50 rounded-lg p-8">
              <div className="text-center">
                <h3 className="text-[#2a4865] text-2xl font-bold mb-4">
                  {pageData.generalInfoTitle}
                </h3>
                <p className="text-gray-700 mb-4">
                  {pageData.generalInfoText}
                </p>
                <div className="flex flex-col md:flex-row justify-center items-center gap-6 text-lg">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.518.76a11.024 11.024 0 008.57 8.57l.76-1.518a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    <span className="font-medium">{pageData.schoolPhone}</span>
                  </div>
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                    <a href={`mailto:${pageData.schoolEmail}`} className="text-[#691e31] hover:underline">
                      {pageData.schoolEmail}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StaffContactPage; 